import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sessionsForCategory, suggestSessions, CATEGORIES, NEEDS, PREFERENCES } from "@/lib/sessions";
import { store } from "@/lib/storage";
import { shareSession } from "@/lib/share";
import { isFreeSession } from "@/lib/entitlement";
import { useAuth } from "@/lib/auth";
import SessionIllustration from "@/components/SessionIllustration";
import { Heart, ChevronLeft, Play, Share2, Sliders, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Suggestions() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const catId = params.get("cat");
  const prefs = store.getPrefs();
  const [favs, setFavs] = useState([]);
  const { entitlement } = useAuth();
  useEffect(() => setFavs(store.getFavorites()), []);

  const cat = catId ? CATEGORIES.find((c) => c.id === catId) : null;
  const rawList = cat ? sessionsForCategory(catId) : suggestSessions(prefs);
  const list = rawList;
  const hasMismatch = list.some((s) => s._mismatch);

  const headerTitle = cat ? cat.label : "Suggestions pour vous";
  const headerSubtitle = cat
    ? cat.description
    : `${NEEDS.find(n => n.id === prefs.need)?.label || "Séances"} · ${prefs.minutes} min`;

  function toggleFav(id, e) {
    e.preventDefault(); e.stopPropagation();
    setFavs(store.toggleFavorite(id));
  }

  async function onShare(session, e) {
    e.preventDefault(); e.stopPropagation();
    const res = await shareSession(session);
    if (res.ok && res.mode === "clipboard") toast.success("Lien copié.");
    else if (!res.ok && res.mode === "unsupported") toast.error("Partage indisponible.");
  }

  return (
    <div className="animate-fade-in pt-6">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-[color:var(--ivoire-muted)] text-sm mb-4">
        <ChevronLeft size={16} /> Retour
      </button>

      {cat && cat.image && (
        <div className="relative rounded-2xl overflow-hidden mb-6 border border-[color:var(--border)]" style={{ aspectRatio: "16 / 9" }}>
          <img src={cat.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080D1A] via-[#0D1B2A]/60 to-transparent" />
          <div className="absolute inset-0 p-4 flex flex-col justify-end">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--sauge-light)]">Thème</p>
            <h1 className="font-serif text-2xl text-[color:var(--ivoire)]">{cat.label}</h1>
          </div>
        </div>
      )}

      {!cat && (
        <>
          <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">{headerTitle}</h1>
          <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">{headerSubtitle}</p>
        </>
      )}

      {cat && (
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-[color:var(--ivoire-muted)]">{list.length} séance{list.length > 1 ? "s" : ""} disponible{list.length > 1 ? "s" : ""}</p>
          <Link to="/personnaliser" data-testid="personalize-link" className="flex items-center gap-1 text-[color:var(--sauge-light)] text-xs">
            <Sliders size={12} /> Personnaliser
          </Link>
        </div>
      )}

      {hasMismatch && !cat && (
        <div data-testid="mismatch-banner" className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-sm text-amber-200 font-medium">Aucune séance ne correspond exactement</p>
          <p className="text-xs text-amber-100/80 mt-1 leading-relaxed">
            Voici les alternatives les plus proches de vos choix ({NEEDS.find(n => n.id === prefs.need)?.label || "besoin"} · {prefs.minutes} min · {PREFERENCES.find(p => p.id === prefs.preference)?.label || "préférence"}).
          </p>
        </div>
      )}

      <div className="mt-4 space-y-3" data-testid="suggestions-list">
        {list.length === 0 && (
          <div className="rounded-xl border border-[color:var(--border)] p-6 text-[color:var(--ivoire-muted)] text-sm">
            Aucune séance ne correspond exactement. Essayez une autre catégorie.
          </div>
        )}
        {list.map((s) => {
          const free = isFreeSession(s.id);
          const locked = !free && !entitlement.premium;
          return (
          <Link
            key={s.id}
            to={locked ? "/premium" : `/lecteur/${s.id}`}
            data-testid={`session-card-${s.id}`}
            className={`block rounded-2xl border ${s._mismatch ? "border-amber-500/30" : "border-[color:var(--border)]"} bg-[#142235]/70 backdrop-blur-md p-5 hover:bg-[#1B2A3E] transition`}
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[color:var(--bg-deep)]/60 flex items-center justify-center ring-1 ring-[color:var(--sauge)]/15 flex-shrink-0 relative">
                <SessionIllustration ambient={s.ambient} method={s.method} size={56} />
                {locked && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[color:var(--bg-deep)] border border-[color:var(--sauge)]/40 flex items-center justify-center">
                    <Lock size={10} className="text-[color:var(--sauge-light)]" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">{s.minutes} min</span>
                  {s._mismatch && <span data-testid={`alt-tag-${s.id}`} className="text-[10px] font-mono tracking-widest uppercase bg-amber-500/15 text-amber-200 px-2 py-0.5 rounded-full">Alternative proche</span>}
                  {free && <span data-testid={`free-tag-${s.id}`} className="text-[10px] font-mono tracking-widest uppercase bg-[color:var(--sauge)]/20 text-[color:var(--sauge-light)] px-2 py-0.5 rounded-full">Découverte</span>}
                  {locked && <span data-testid={`locked-tag-${s.id}`} className="text-[10px] font-mono tracking-widest uppercase text-[color:var(--ivoire-subtle)]">Premium</span>}
                </div>
                <h3 className="font-serif text-xl text-[color:var(--ivoire)] mt-1 leading-tight">{s.title}</h3>
                <p className="text-xs text-[color:var(--ivoire-muted)] mt-1 leading-relaxed">{s.description}</p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid={`fav-toggle-${s.id}`}
                    onClick={(e) => toggleFav(s.id, e)}
                    aria-label={favs.includes(s.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                    className="w-8 h-8 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:bg-[#1B2A3E]"
                  >
                    <Heart size={14} className={favs.includes(s.id) ? "fill-[color:var(--sauge-light)] text-[color:var(--sauge-light)]" : "text-[color:var(--ivoire-muted)]"} />
                  </button>
                  <button
                    data-testid={`share-${s.id}`}
                    onClick={(e) => onShare(s, e)}
                    aria-label="Envoyer à un proche"
                    className="w-8 h-8 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:bg-[#1B2A3E] text-[color:var(--ivoire-muted)]"
                  >
                    <Share2 size={14} />
                  </button>
                  <div className="ml-auto w-9 h-9 rounded-full bg-[color:var(--sauge)]/20 flex items-center justify-center">
                    {locked ? <Lock size={14} className="text-[color:var(--sauge-light)]" /> : <Play size={14} className="text-[color:var(--sauge-light)]" fill="currentColor" />}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )})}
      </div>
    </div>
  );
}
